"use client";
import Button from "@/components/common/button";
import { useEffect, useState } from "react";
import { cookiesManagerEN } from "@/assets/dictionaries/en/manage-content";
import { cookiesManagerES } from "@/assets/dictionaries/es/manage-content";

const CookiesManage = () => {
  const [hasCookieConsent, setHasCookieConsent] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false); // New state to track component mounting

  useEffect(() => {
    setIsMounted(true); // Set to true after the component has mounted

    const storedValue = localStorage.getItem("enableCookies");
    if (storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        setHasCookieConsent(
          parsed.acceptedCookies === "true" ? "true" : "false",
        );
      } catch (e) {
        setHasCookieConsent(null);
      }
    } else {
      setHasCookieConsent(null);
    }

    // Listen for changes in localStorage (across tabs or windows)
    const handleStorageChange = () => {
      const storedValue = localStorage.getItem("enableCookies");
      if (storedValue) {
        try {
          const parsed = JSON.parse(storedValue);
          setHasCookieConsent(
            parsed.acceptedCookies === "true" ? "true" : "false",
          );
          window.location.reload(); // Reload the page
        } catch (e) {
          setHasCookieConsent(null);
          window.location.reload();
        }
      } else {
        setHasCookieConsent(null);
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []); // Empty dependency array ensures this effect runs only once

  const toggleLocalStorage = () => {
    const newConsent = hasCookieConsent === "true" ? "false" : "true";
    localStorage.setItem(
      "enableCookies",
      `{"acceptedCookies":"${newConsent}"}`,
    );
    window.location.reload();
  };

  const clearStorage = () => {
    localStorage.setItem("enableCookies", `{"acceptedCookies":"${false}"}`);
    window.location.reload();
  };

  const [cookiesManageContent, setCookiesManageContent] =
    useState(cookiesManagerEN); // Default to English

  useEffect(() => {
    // Get the language from localStorage
    const lang = localStorage.getItem("selectedOption") || "EN"; // Default to EN if not found

    // Set the content based on the language
    switch (lang.toUpperCase()) {
      case "ES":
        setCookiesManageContent(cookiesManagerES);
        break;
      case "EN":
      default:
        setCookiesManageContent(cookiesManagerEN);
        break;
    }
  }, []); // Only run on mount

  if (!isMounted) {
    return (
      <section className="px-page-xl">
        <div className="flex flex-col justify-center items-center h-[385.6px]">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-4 border-t-gbci-accent"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-8 px-page-xl">
      <div className="mt- flex flex-col items-center justify-center gap-8">
        <div className="flex justify-center items-center gap-8">
          <div className="text-xl font-bold text-center">
            {cookiesManageContent.cookies_first}
          </div>
          <div className="text-xl font-bold flex gap-4">
            <button
              onClick={toggleLocalStorage}
              className={`w-full bg-transparent border my-2 hover:brightness-75 transition-all duration-150 p-4 font-bold rounded-md shadow-md ${hasCookieConsent === "true" ? "border-red-500 text-red-500" : "border-gbci-primary text-gbci-primary"}`}
            >
              {hasCookieConsent === "true"
                ? `${cookiesManageContent.cookies_button_reject}`
                : `${cookiesManageContent.cookies_button_accept}`}
            </button>
          </div>
        </div>
        <div className="flex flex-col w-full mb-8 mt-16 px-8 md:px-64">
          <Button onClick={clearStorage}>
            {cookiesManageContent.cookies_clear_all}
          </Button>
        </div>
        <div className="text-xl text-center my-4">
          {cookiesManageContent.cookies_information}
        </div>
      </div>
    </section>
  );
};

export default CookiesManage;
